import { NgModule, inject } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { Login } from './pages/auth/login/login';
import { Signup } from './pages/auth/signup/signup';
import { MainLayout } from './shared/main-layout/main-layout';
import { Home } from './pages/home/home';
import { Playground } from './pages/playground/playground';
import { Rag } from './pages/playground/rag/rag';
import { Answer } from './pages/playground/answer/answer';
import { Search } from './pages/playground/search/search';
import { Similar } from './pages/playground/similar/similar';
import { Image } from './pages/playground/image/image';
import { Legal } from './pages/playground/legal/legal';
import { Pages } from './pages/playground/pages/pages';
import { DocsLayout } from './shared/docs-layout/docs-layout';
import { Docs } from './pages/docs/overview/docs';
import { DocsQuickstart } from './pages/docs/quickstart/docs-quickstart';
import { DocsAuthentication } from './pages/docs/authentication/docs-authentication';
import { DocsMakingACall } from './pages/docs/making-a-call/docs-making-a-call';
import { DocsPricingLimits } from './pages/docs/pricing-limits/docs-pricing-limits';
import { DocsErrors } from './pages/docs/errors/docs-errors';
import { DocsAccountFilters } from './pages/docs/account-filters/docs-account-filters';
import { DocsApiReferenceIndex } from './pages/docs/api-reference/index/docs-api-reference-index';
import { DocsApiReferenceDetail } from './pages/docs/api-reference/detail/docs-api-reference-detail';
import { DocsChangelog } from './pages/docs/changelog/docs-changelog';
import { ApiKeys } from './pages/api-keys/list/api-keys';
import { Billing } from './pages/billing/overview/billing';
import { BillingUsage } from './pages/billing/usage/billing-usage';
import { BillingTransactions } from './pages/billing/transactions/billing-transactions';
import { WalletReturn } from './pages/billing/wallet-return/wallet-return';
import { Settings } from './pages/settings/settings';
import { Support } from './pages/support/support';
import { ConfirmEmail } from './pages/auth/confirm-email/confirm-email';
import { CheckEmail } from './pages/auth/check-email/check-email';
import { ForgotPassword } from './pages/auth/forgot-password/forgot-password';
import { ResetPassword } from './pages/auth/reset-password/reset-password';
import { AdminLayout } from './pages/admin/admin-layout/admin-layout';
import { AdminOverview } from './pages/admin/overview/admin-overview';
import { AdminOrganizations } from './pages/admin/organizations/list/admin-organizations';
import { AdminOrganizationDetail } from './pages/admin/organizations/detail/admin-organization-detail';
import { AdminCalls } from './pages/admin/calls/admin-calls';
import { AdminTransactions } from './pages/admin/transactions/admin-transactions';
import { AdminUsers } from './pages/admin/users/admin-users';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { superadminGuard } from './core/guards/superadmin.guard';

const SUFFIX = ' · -mila.';

const routes: Routes = [
  { path: 'login', component: Login, title: 'Log in' + SUFFIX, canActivate: [guestGuard] },
  { path: 'signup', component: Signup, title: 'Sign up' + SUFFIX, canActivate: [guestGuard] },
  { path: 'check-email', component: CheckEmail, title: 'Check your email' + SUFFIX },
  { path: 'auth/confirm', component: ConfirmEmail, title: 'Confirm email' + SUFFIX },
  { path: 'forgot-password', component: ForgotPassword, title: 'Forgot password' + SUFFIX, canActivate: [guestGuard] },
  { path: 'auth/reset-password', component: ResetPassword, title: 'Reset password' + SUFFIX },
  {
    path: 'docs',
    component: DocsLayout,
    children: [
      { path: '', component: Docs, title: 'Docs' + SUFFIX },
      { path: 'quickstart', component: DocsQuickstart, title: 'Quickstart · Docs' + SUFFIX },
      { path: 'authentication', component: DocsAuthentication, title: 'Authentication · Docs' + SUFFIX },
      { path: 'making-a-call', component: DocsMakingACall, title: 'Making a call · Docs' + SUFFIX },
      { path: 'pricing-limits', component: DocsPricingLimits, title: 'Pricing & limits · Docs' + SUFFIX },
      { path: 'errors', component: DocsErrors, title: 'Errors · Docs' + SUFFIX },
      { path: 'account-filters', component: DocsAccountFilters, title: 'Account & filters · Docs' + SUFFIX },
      {
        path: 'api-reference',
        children: [
          { path: '', component: DocsApiReferenceIndex, title: 'API Reference · Docs' + SUFFIX },
          { path: ':id', component: DocsApiReferenceDetail, title: 'API Reference · Docs' + SUFFIX },
        ],
      },
      { path: 'changelog', component: DocsChangelog, title: 'Changelog · Docs' + SUFFIX },
    ],
  },
  {
    path: '',
    component: MainLayout,
    canActivate: [authGuard],
    children: [
      { path: '', component: Home, title: 'Home' + SUFFIX },
      {
        path: 'playground',
        component: Playground,
        title: 'Playground' + SUFFIX,
        children: [
          { path: '', redirectTo: 'rag', pathMatch: 'full' },
          { path: 'rag', component: Rag, title: 'RAG · Playground' + SUFFIX },
          { path: 'answer', component: Answer, title: 'Answer · Playground' + SUFFIX },
          { path: 'search', component: Search, title: 'Search · Playground' + SUFFIX },
          { path: 'similar', component: Similar, title: 'Similar · Playground' + SUFFIX },
          { path: 'image', component: Image, title: 'Image · Playground' + SUFFIX },
          { path: 'legal', component: Legal, title: 'Legal · Playground' + SUFFIX, canActivate: [superadminGuard] },
          { path: 'pages', component: Pages, title: 'Pages · Playground' + SUFFIX, canActivate: [superadminGuard] },
        ],
      },
      { path: 'api-keys', component: ApiKeys, title: 'API Keys' + SUFFIX },
      // Creating a key is a modal on the list now; old links and bookmarks
      // land there with it open (see ApiKeys.createModal).
      { path: 'api-keys/create', redirectTo: () => inject(Router).createUrlTree(['/api-keys'], { queryParams: { create: '1' } }) },
      { path: 'billing', component: Billing, title: 'Billing' + SUFFIX },
      { path: 'billing/usage', component: BillingUsage, title: 'Usage · Billing' + SUFFIX },
      { path: 'billing/transactions', component: BillingTransactions, title: 'Transactions · Billing' + SUFFIX },
      { path: 'wallet/success', component: WalletReturn, data: { status: 'success' }, title: 'Payment successful' + SUFFIX },
      { path: 'wallet/cancelled', component: WalletReturn, data: { status: 'cancelled' }, title: 'Payment cancelled' + SUFFIX },
      { path: 'settings', component: Settings, title: 'Settings' + SUFFIX },
      { path: 'support', component: Support, title: 'Support' + SUFFIX },
      {
        path: 'admin',
        component: AdminLayout,
        canActivate: [superadminGuard],
        children: [
          { path: '', component: AdminOverview, title: 'Overview · Platform' + SUFFIX },
          { path: 'organizations', component: AdminOrganizations, title: 'Organizations · Platform' + SUFFIX },
          { path: 'organizations/:slug', component: AdminOrganizationDetail, title: 'Organization · Platform' + SUFFIX },
          { path: 'calls', component: AdminCalls, title: 'Calls · Platform' + SUFFIX },
          { path: 'transactions', component: AdminTransactions, title: 'Transactions · Platform' + SUFFIX },
          { path: 'users', component: AdminUsers, title: 'Users · Platform' + SUFFIX },
        ],
      },
    ]
  },
  { path: '**', redirectTo: '' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    scrollPositionRestoration: 'enabled',
    anchorScrolling: 'enabled',
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
